import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {Component, OnInit} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';
import {
    CompanyContactCosts,
    CompanyCostConfiguration,
    CompanyCostSet,
    CompanyDto,
    CompanyOrderCosts,
    CompanyScheduledCost,
    ConfiguratorCompanyService,
    ConfiguratorDatastoreService,
    DatastoreDto,
    Remuneration
} from '@tj/tj-dmp-lib-api';
import {MenuItem, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {configurationRemunerations, RemunerationTransactionType} from '../../invoice/remuneration-configuration';
import {RemunerationAccordionComponent} from '../../invoice/remuneration-accordion/remuneration-accordion.component';
import {showValidationError} from '../../form-validation';

type RemunerationWithFilters = Remuneration & {
    typeTransaction?: RemunerationTransactionType;
    flightCompagny?: string | null;
    flightDepartureCity?: string | null;
    flightDepartureAirport?: string | null;
    flightArrivalCity?: string | null;
    flightArrivalAirport?: string | null;
};

type CompanyInvoicePricing = CompanyDto & {
    vatValue?: number;
    priceDisplayMode?: 'HT' | 'TTC';
};

@Component({
    selector: 'dmp-company-edit',
    templateUrl: './company-edit.component.html',
    styleUrls: ['./company-edit.component.scss'],
    standalone: false
})
export class CompanyEditComponent implements OnInit {
    get locale(): string { return this.translate.getCurrentLang() === 'en' ? 'en-GB' : 'fr-FR'; }

    readonly languageOptions = [
        {label: this.translate.instant('invoicing.common.french'), value: 'fr'},
        {label: this.translate.instant('invoicing.common.english'), value: 'en'}
    ];
    breadcrumb: MenuItem[] = [
        { label: this.translate.instant('menu.invoice.label') },
        { label: this.translate.instant('menu.invoice.companies') },
        {label: this.translate.instant('invoicing.common.create')},
    ];
    description: string;

    loaded = false;

    actual: CompanyInvoicePricing;

    form: UntypedFormGroup;
    submitted = false;
    tenantCode: string;

    group: DatastoreDto;

    constructor(
        private fb: UntypedFormBuilder,
        protected readonly route: ActivatedRoute,
        protected configuratorCompanyService: ConfiguratorCompanyService,
        protected datastoreResourceService: ConfiguratorDatastoreService,
        private messageService: MessageService,
        private router: Router,
        private readonly translate: TranslateService
    ) {}

    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    get remunerations() {
        return this.form.get('remunerations') as FormArray;
    }

    get leadRemunerations() {
        return this.form.get('leadRemunerations') as FormArray;
    }

    get costsForm(): UntypedFormGroup {
        return this.form.get('costs') as UntypedFormGroup;
    }

    isInvalid(path: string): boolean {
        return showValidationError(this.form.get(path), this.submitted);
    }

    async ngOnInit() {
        this.tenantCode = this.route.snapshot.paramMap.get('tenant');
        const id = this.route.snapshot.paramMap.get('id');
        const initialPartner = !id ? this.route.snapshot.queryParamMap?.get('partner')?.trim() : null;

        this.description = this.translate.instant('invoicing.companies.create');
        this.form = this.fb.group({
            id: [''],
            tenant: [this.tenantCode, Validators.required],
            affiliation: [initialPartner || '', Validators.required],
            startDate: ['', Validators.required],
            endDate: ['', Validators.required],
            name: ['', Validators.required],
            department: [''],
            address1: ['', Validators.required],
            address2: [''],
            zipcode: ['', Validators.required],
            city: ['', Validators.required],
            country: ['', Validators.required],
            tva: ['', Validators.required],
            vatValue: [null, [Validators.required, Validators.min(0), Validators.max(50)]],
            priceDisplayMode: [null, Validators.required],
            language: ['fr', Validators.required],
            siren: ['', Validators.required],
            costs: this.buildCompanyCostsForm(),
            remunerations: this.fb.array([], this.companyRemunerationsValidator()),
            leadRemunerations: this.fb.array([], this.companyRemunerationsValidator(false)),
        }, {validators: this.contractDateRangeValidator()});
        if (id) {
            this.description = this.translate.instant('invoicing.companies.edit');
            this.breadcrumb[2].label = this.translate.instant('invoicing.common.edition');
            this.configuratorCompanyService
                .getCompanyUsingGET(this.tenantCode, id, 'response')
                .subscribe({
                    next: (res: HttpResponse<CompanyDto>) => {
                        if (!res.body) {
                            this.initializeCreationFromRouteId(id);
                            return;
                        }
                        this.actual = res.body;
                        this.form.patchValue({
                            id: this.actual.id,
                            tenant: this.actual.datastoreVo.code,
                            affiliation: this.actual.affiliation,
                            startDate: this.toDateOrNull(this.actual.startDate),
                            endDate: this.toDateOrNull(this.actual.endDate),
                            name: this.actual.name,
                            department: this.actual.department,
                            address1: this.actual.address1,
                            address2: this.actual.address2,
                            zipcode: this.actual.zipcode,
                            city: this.actual.city,
                            country: this.actual.country,
                            tva: this.actual.tva,
                            vatValue: this.actual.vatValue,
                            priceDisplayMode: this.actual.priceDisplayMode,
                            language: this.actual.language || 'fr',
                            siren: this.actual.siren
                        });
                        this.form.setControl('costs', this.buildCompanyCostsForm(this.actual.costs));
                        this.setCompanyRemunerationsFromApi(this.actual.remunerations || []);
                    },
                    error: () => {
                        this.initializeCreationFromRouteId(id);
                    }
                });
        }

        this.loaded = true;
    }

    private initializeCreationFromRouteId(affiliation: string): void {
        this.description = this.translate.instant('invoicing.companies.create');
        this.breadcrumb[2].label = this.translate.instant('invoicing.common.create');
        this.form.patchValue({
            id: '',
            affiliation
        });
        this.form.setControl('costs', this.buildCompanyCostsForm());
        this.form.setControl('remunerations', this.fb.array([], this.companyRemunerationsValidator()));
        this.form.setControl('leadRemunerations', this.fb.array([], this.companyRemunerationsValidator(false)));
    }

    submit() {
        this.submitted = true;
        this.form.markAllAsTouched();
        if (this.form.invalid) { return; }
        const value = this.form.value;
        value.startDate = this.formatDate(value.startDate);
        value.endDate = this.formatDate(value.endDate);
        const costs = this.toCompanyCostsPayload(this.costsForm.getRawValue());
        const remunerationValues = [
            ...this.toRemunerationPayload(value.remunerations, 'command'),
            ...this.toRemunerationPayload(value.leadRemunerations, 'lead')
        ];
        this.debugRemunerationPayload(remunerationValues);
        const company: CompanyInvoicePricing = {
            id: value.id,
            datastoreVo: { code: value.tenant },
            startDate: value.startDate,
            endDate: value.endDate,
            name: value.name,
            department: value.department,
            address1: value.address1,
            address2: value.address2,
            zipcode: value.zipcode,
            city: value.city,
            country: value.country,
            tva: value.tva,
            vatValue: value.vatValue,
            priceDisplayMode: value.priceDisplayMode,
            language: value.language,
            siren: value.siren,
            contacts: [],
            affiliation: value.affiliation,
            costs,
            remunerations: remunerationValues
        };
        if (this.actual) {
            this.configuratorCompanyService
                .updateCompanyUsingPUT(this.tenantCode, company)
                .subscribe(
                    () => {
                        const navigationExtras: NavigationExtras = {
                            state: {message: 'success_updated'},
                        };
                        this.router.navigate(
                            [`/h/invoicing/company`],
                            navigationExtras
                        );
                    }
                );
        } else {
            this.configuratorCompanyService
                .createCompanyUsingPOST(this.tenantCode, company)
                .subscribe(
                    () => {
                        const navigationExtras: NavigationExtras = {
                            state: {message: 'success_created'},
                        };
                        this.router.navigate(
                            [`/h/invoicing/company`],
                            navigationExtras
                        );
                    }
                );
        }
    }

    private formatDate(date: Date): string {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private toDateOrNull(value: Date | string | null | undefined): Date | null {
        if (!value) {
            return null;
        }
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const [year, month, day] = value.split('-').map(Number);
            const localDate = new Date(year, month - 1, day);
            return localDate.getFullYear() === year && localDate.getMonth() === month - 1
                && localDate.getDate() === day ? localDate : null;
        }
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    private contractDateRangeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const start = this.toDateOrNull(control.get('startDate')?.value);
            const end = this.toDateOrNull(control.get('endDate')?.value);
            if (!start || !end) {
                return null;
            }
            const dateKey = (date: Date): number =>
                date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
            return dateKey(end) < dateKey(start) ? {contractDateRange: true} : null;
        };
    }

    private buildCompanyCostsForm(costs?: CompanyCostConfiguration): UntypedFormGroup {
        return this.fb.group({
            purchaseCosts: this.buildCostSetForm(costs?.purchaseCosts),
            saleCosts: this.buildCostSetForm(costs?.saleCosts),
            scheduledCosts: this.fb.array(
                (costs?.scheduledCosts || []).map(scheduledCost => this.buildScheduledCostForm(scheduledCost))
            )
        });
    }

    private buildCostSetForm(costSet?: CompanyCostSet): UntypedFormGroup {
        return this.fb.group({
            contactCosts: this.buildContactCostsForm(costSet?.contactCosts),
            orderCosts: this.buildOrderCostsForm(costSet?.orderCosts)
        });
    }

    private buildContactCostsForm(contactCosts?: CompanyContactCosts): UntypedFormGroup {
        return this.fb.group({
            defaultPrice: [this.toOptionalNumber(contactCosts?.defaultPrice), Validators.min(0)],
            flightPrice: [this.toOptionalNumber(contactCosts?.flightPrice), Validators.min(0)],
            hotelPrice: [this.toOptionalNumber(contactCosts?.hotelPrice), Validators.min(0)],
            carPrice: [this.toOptionalNumber(contactCosts?.carPrice), Validators.min(0)],
            parkingPrice: [this.toOptionalNumber(contactCosts?.parkingPrice), Validators.min(0)]
        });
    }

    private buildOrderCostsForm(orderCosts?: CompanyOrderCosts): UntypedFormGroup {
        return this.fb.group({
            flightPrice: [this.toOptionalNumber(orderCosts?.flightPrice), Validators.min(0)],
            hotelPrice: [this.toOptionalNumber(orderCosts?.hotelPrice), Validators.min(0)],
            carPrice: [this.toOptionalNumber(orderCosts?.carPrice), Validators.min(0)],
            parkingPrice: [this.toOptionalNumber(orderCosts?.parkingPrice), Validators.min(0)]
        });
    }

    private buildScheduledCostForm(scheduledCost?: CompanyScheduledCost): UntypedFormGroup {
        return this.fb.group({
            costType: [scheduledCost?.costType || null, Validators.required],
            dataType: [scheduledCost?.dataType || null, Validators.required],
            product: [scheduledCost?.product || null, Validators.required],
            price: [this.toOptionalNumber(scheduledCost?.price), [Validators.required, Validators.min(0)]],
            effectiveDate: [this.toDateOrNull(scheduledCost?.effectiveDate), Validators.required],
            applied: [{value: scheduledCost?.applied || false, disabled: true}],
            appliedAt: [{value: this.toDateOrNull(scheduledCost?.appliedAt), disabled: true}]
        });
    }

    private toCompanyCostsPayload(costs: any): CompanyCostConfiguration {
        return {
            purchaseCosts: this.toCostSetPayload(costs?.purchaseCosts),
            saleCosts: this.toCostSetPayload(costs?.saleCosts),
            scheduledCosts: this.toScheduledCostsPayload(costs?.scheduledCosts)
        };
    }

    private toCostSetPayload(costSet: any): CompanyCostSet {
        return {
            contactCosts: this.toContactCostsPayload(costSet?.contactCosts),
            orderCosts: this.toOrderCostsPayload(costSet?.orderCosts)
        };
    }

    private toContactCostsPayload(contactCosts: any): CompanyContactCosts {
        return {
            defaultPrice: this.toOptionalNumber(contactCosts?.defaultPrice),
            flightPrice: this.toOptionalNumber(contactCosts?.flightPrice),
            hotelPrice: this.toOptionalNumber(contactCosts?.hotelPrice),
            carPrice: this.toOptionalNumber(contactCosts?.carPrice),
            parkingPrice: this.toOptionalNumber(contactCosts?.parkingPrice)
        };
    }

    private toOrderCostsPayload(orderCosts: any): CompanyOrderCosts {
        return {
            flightPrice: this.toOptionalNumber(orderCosts?.flightPrice),
            hotelPrice: this.toOptionalNumber(orderCosts?.hotelPrice),
            carPrice: this.toOptionalNumber(orderCosts?.carPrice),
            parkingPrice: this.toOptionalNumber(orderCosts?.parkingPrice)
        };
    }

    private toScheduledCostsPayload(scheduledCosts: unknown): CompanyScheduledCost[] {
        if (!Array.isArray(scheduledCosts)) {
            return [];
        }

        return scheduledCosts.map((scheduledCost: any) => ({
            costType: scheduledCost.costType,
            dataType: scheduledCost.dataType,
            product: scheduledCost.product,
            price: this.toOptionalNumber(scheduledCost.price),
            effectiveDate: this.formatDateValue(scheduledCost.effectiveDate),
            applied: scheduledCost.applied || false,
            appliedAt: scheduledCost.appliedAt
        })) as CompanyScheduledCost[];
    }

    private toOptionalNumber(value: unknown): number | undefined {
        if (value === null || value === undefined || value === '') {
            return undefined;
        }
        const numberValue = typeof value === 'number' ? value : Number(value);
        return Number.isNaN(numberValue) ? undefined : numberValue;
    }

    private formatDateValue(value: Date | string | null | undefined): string {
        if (!value) {
            return null;
        }
        if (typeof value === 'string') {
            return value.length >= 10 ? value.substring(0, 10) : value;
        }
        return this.formatDate(value);
    }

    private toRemunerationPayload(
        remunerations: unknown, transactionType: RemunerationTransactionType
    ): RemunerationWithFilters[] {
        return configurationRemunerations(Array.isArray(remunerations) ? remunerations : [], transactionType);
    }

    private setCompanyRemunerationsFromApi(remunerations: Remuneration[]): void {
        this.form.setControl('remunerations', this.fb.array(
            this.buildProviderGroups(remunerations, 'command'), this.companyRemunerationsValidator()));
        this.form.setControl('leadRemunerations', this.fb.array(
            this.buildProviderGroups(remunerations, 'lead'), this.companyRemunerationsValidator(false)));
    }

    private buildProviderGroups(remunerations: Remuneration[], transactionType: RemunerationTransactionType) {
        const rulesByProvider = new Map<string, RemunerationWithFilters[]>();
        remunerations.filter(rule => (rule as RemunerationWithFilters).typeTransaction === transactionType)
            .forEach(rule => {
            const provider = rule.provider || '';
            const providerRules = rulesByProvider.get(provider) || [];
            providerRules.push(rule as RemunerationWithFilters);
            rulesByProvider.set(provider, providerRules);
        });

        return Array.from(rulesByProvider.entries()).map(([provider, rules]) =>
            RemunerationAccordionComponent.buildCompanyProviderGroup(
                this.fb, provider || null, rules, undefined, transactionType)
        );
    }

    private companyRemunerationsValidator(required = true): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const array = control as FormArray;
            if (!array || array.length === 0) {
                return required ? {requiredRemunerations: true} : null;
            }
            const providers = array.controls
                .map(item => item.get('provider')?.value)
                .filter(provider => typeof provider === 'string' && provider.length > 0)
                .map(provider => provider.trim().toUpperCase());
            return new Set(providers).size === providers.length ? null : {duplicateProviders: true};
        };
    }

    private debugRemunerationPayload(remunerations: RemunerationWithFilters[]): void {
        try {
            const serialized = JSON.stringify(remunerations);
            console.debug('[CompanyEdit] Remunerations payload (JSON):', serialized);
        } catch (error) {
            console.error('[CompanyEdit] Remunerations payload is not serializable:', error);
        }
    }
}
